import { AfterViewInit, ChangeDetectionStrategy, Component, computed, inject, input, output, signal, ViewChild } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MaterialModule } from '@nx-customer-apps/shared/ui';
import { ComponentsModule, DrawerComponent } from '@nx-customer-apps/components';
import { RoomListItem, RoomType } from '../../models';
import { AddRoomsEvent, Level } from '../../models';
import { LevelNamePipe } from '../../pipes/level-name.pipe';

@Component({
    selector: 'rbr-add-rooms',
    templateUrl: './add-rooms.component.html',
    styleUrl: './add-rooms.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MaterialModule, ComponentsModule, TranslatePipe, LevelNamePipe]
})
export class AddRoomsComponent implements AfterViewInit {
    private translate = inject(TranslateService);
    private readonly allRoomTypes = Object.values(RoomType);

    level = input<Level | null>();
    levels = input<Level[]>([]);
    hasUpperLevels = computed(() => this.levels().some(level => level.position > 0));
    hasLowerLevels = computed(() => this.levels().some(level => level.position < 0));
    confirmButtonDisabled = computed(() => Object.keys(this.roomCounts()).length === 0);
    roomsData = input<RoomListItem[] | null | undefined>(undefined);
    addRooms = output<AddRoomsEvent>();
    roomCounts = signal<Partial<Record<RoomType, number>>>({});
    roomTypes = [...this.allRoomTypes];
    searchTerm = '';

    @ViewChild('drawer', { static: true }) drawer: DrawerComponent;

    ngAfterViewInit(): void {
        // open drawer if this is the first level being added
        if (this.levels().length === 1) {
            this.drawer.open();
        }
    }

    openDrawer(): void {
        this.drawer.open();
    }

    search(): void {
        const searchTerm = this.searchTerm.trim().toLowerCase();

        if (!searchTerm) {
            this.roomTypes = [...this.allRoomTypes];
            return;
        }

        this.roomTypes = this.allRoomTypes.filter(roomType =>
            this.translate.instant(`RBR.ROOM_LIST.${roomType}`).toLowerCase().includes(searchTerm)
        );
    }

    onRoomCountChange(roomType: RoomType, count: number): void {
        if (count > 0) {
            this.roomCounts.update(current => ({
                ...current,
                [roomType]: count
            }));
            return;
        }

        this.roomCounts.update(current => {
            const { [roomType]: _toDelete, ...rest } = current;
            return rest;
        });
    }

    confirm(): void {
        const level = this.level();

        if (!level) {
            return;
        }

        this.addRooms.emit({
            rooms: this.roomCounts(),
            levelId: level.id,
            levelPosition: level.position
        });

        this.roomCounts.set({});
        this.drawer.close();
    }
}
